import { Component, h, Prop, State, Element } from '@stencil/core';
import type { ComponentInterface } from '@stencil/core';
import { guid, _BREAKPOINTS_ } from 'utils';
import Glide from '@glidejs/glide';

@Component({
  tag: 'app-carousel',
  styleUrl: 'carousel.scss'
})
export class Carousel implements ComponentInterface {

  @Element() root: HTMLElement;

  /**
  * Display flag for showing the bullet navigation. Pass true to show.
  */
  @Prop() bulletNav: boolean;

  /**
   * If true, bullet nav will display below slides. Default will show bullet
   * nav within slides area.
   */
  @Prop() displayBulletNavExternally: boolean;

  /**
   * Amount of items to display per slide. Default will show 1 item per slide.
   */
  @Prop({
    mutable: true,
    reflect: true,
  }) perSlide: string;

  @Prop() perSlideTablet: string;
  @Prop() perSlideMobile: string;

  /**
   * On multi item slides, set focus to middle item.
   */
  @Prop() centerFocus: boolean;

  /**
   * If set, slides will autoplay and a play/pause button will appear.
   */
  @Prop() autoPlay: boolean;

  /**
   * Override the default duration for slide times while on autoplay.
   */
  @Prop() autoPlayDuration: string = "8000";

  /**
   * The type of carousel
   */
  @Prop() carouselType: 'carousel' | 'slider' = 'carousel';

  /**
   * Disables swipe interface is true
   */
  @Prop() disableSwipe: boolean = false;

  /**
   * Controls paused/autoplay state of carousel.
   */
  @State() paused: boolean = false;

  /**
   * Show/Hide controls based on slide count & breakpoints
   */
  @State() hideControls = false;

  /**
   * Allows arrows to be disabled to stop infinite loop OR rewind
   */
  @Prop() disableArrows: boolean = true;

  private id: string = 'hero-carousel-' + guid();
  private initialRenderComplete = false;
  private carousel: any = null;

  componentWillLoad() {
    this.root.querySelector('.app-slides')!.setAttribute('id', this.id + '-slides');
    const slides = this.root.querySelector(".glide__slides")!.children;
    
    this.viewControl();

    //After checking whether or not controls should be shown initially, listen for breakpoints when the screen size is changed.
    window.matchMedia("(min-width: 1141px").addEventListener('change', (e) => {
      if(e.matches){
        if(slides.length  <= parseInt(this.perSlide, 10)){
          this.hideControls = true;
        }else {
          this.hideControls = false;
        }
      }
    });

    window.matchMedia("(max-width: 1140px").addEventListener('change', (e) => {
      if(e.matches){
        if(slides.length <= parseInt(this.perSlideTablet, 10)){
          this.hideControls = true;
        }else {
          this.hideControls = false;
        }
      }
    });
    window.matchMedia("(max-width: 960px").addEventListener('change', (e) => {
      if(e.matches){
        if(slides.length <= parseInt(this.perSlideMobile, 10)){
          this.hideControls = true;
        }else {
          this.hideControls = false;
        }
      }
    })
  }

  componentDidRender() {
    if (!this.initialRenderComplete) {
      this.setUp();
    }
  }


  //Init check whether or not controls should be shown
  viewControl(){
    const slides = this.root.querySelector('.glide__slides')!.children;

    if(window.innerWidth > 1140){
      if(slides.length  <= parseInt(this.perSlide, 10)){
        this.hideControls = true;
      }else {
        this.hideControls = false;
      }
    }else if (window.innerWidth > 960 && window.innerWidth <= 1140){
      if(slides.length  <= parseInt(this.perSlideTablet, 10)){
        this.hideControls = true;
      }else {
        this.hideControls = false;
      }
    } else {
      if(slides.length  <= parseInt(this.perSlideMobile, 10)){
        this.hideControls = true;
      }else {
        this.hideControls = false;
      }
    }

   
  }

  private setUp() {
    if (document.querySelector('body.is-editing')) {
      return;
    }

    const instance = document.getElementById(this.id)!;
    const bulletsWrapper = instance.querySelector('.glide__bullets')!;
    const slides = instance.querySelector('.glide__slides')!.children;

    if (bulletsWrapper) {
      for (let i = 0; i < slides.length; i++) {
        bulletsWrapper.innerHTML += `
        <button class="glide__bullet" data-glide-dir="=${i}" aria-label="Go to slide ${i + 1}" aria-controls=${this.id + '-slides'}></button>`;
      }
    }

    if (this.perSlide) {
      this.carousel = new Glide(`#${this.id}`, {
        type: this.carouselType,
        perView: parseInt(this.perSlide, 10),
        focusAt: this.centerFocus ? 'center' : '0',
        autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : false,
        hoverpause: false,
        swipeThreshold: this.disableSwipe ? false : 80,
        dragThreshold: this.disableSwipe ? false : 120,
        bound: true,
        gap: 20,
        breakpoints: {
          // ENABLE IF USING 4-UP
          // 1400: {
          //   perView: 3
          // },
          1140: {
            perView: this.perSlideTablet ? parseInt(this.perSlideTablet, 10) : 2
          },
          960: {
            perView: this.perSlideMobile ? parseInt(this.perSlideTablet, 10) : 1
          }

          
        }
      }).on('mount.before', () => {
        instance.querySelector('button:first-child')!.setAttribute('disabled', 'true');
    }).mount();
    } else {
      this.carousel = new Glide(`#${this.id}`, {
        autoplay: this.autoPlay ? parseInt(this.autoPlayDuration, 10) : false,
        hoverpause: false
      }).mount();
    }


      /**
         * Handles enabling / disabling the
         * Buttons for this particular slider.
         *
         */
        if (this.disableArrows === true) {
            this.carousel.on('run.after', () => {
                if (this.carousel.index < slides.length - this.carousel.settings.perView || this.carousel.index > 0) {
                    instance.querySelector('button:first-child')!.removeAttribute('disabled');
                    instance.querySelector('button:last-child')!.removeAttribute('disabled');

                    if (this.carousel.index === slides.length - this.carousel.settings.perView) {
                        instance.querySelector('button:last-child')!.setAttribute('disabled', 'true');
                    }

                    if (this.carousel.index === 0) {
                        instance.querySelector('button:first-child')!.setAttribute('disabled', 'true');
                    }
                }
            });
        }



    /**
     * On resize, ensures that the slider / 
     * carousel does not end up in a state 
     * where there is whitespace to the right
     * side of the last element
     * 
     * This assumes that the slider will break
     * @ the following resolutions --
     * 
     * > _BREAKPOINTS_.xl => 4up
     * > _BREAKPOINTS_.lg => 3up
     * > _BREAKPOINTS_.md => 2up
     * < _BREAKPOINTS_.sm => 1up
     */
    window.addEventListener('resize', () => {
      if (this.carousel.settings.perView !== parseInt(this.perSlide, 10)) {
        if (this.carousel.settings.perView > parseInt(this.perSlide, 10)) {
          if (window.innerWidth < _BREAKPOINTS_.md) {
            if (this.carousel.index === slides.length - 1) {
              this.carousel.go(`=${this.carousel.index - 1}`);
              instance.querySelector('button:last-child')!.setAttribute('disabled', 'true');
            }
          } else if (window.innerWidth < _BREAKPOINTS_.lg) {
            if (this.carousel.index === slides.length - 2) {
              this.carousel.go(`=${this.carousel.index - 1}`);
              instance.querySelector('button:last-child')!.setAttribute('disabled', 'true');
            }
            if (this.carousel.index === slides.length - 3) {
              instance.querySelector('button:last-child')!.setAttribute('disabled', 'true');
            }
          } 
          // ENABLE IF USING 4-UP
          // else if (window.innerWidth > _BREAKPOINTS_.xl) {
          //   if (this.carousel.index === slides.length - 3) {
          //     this.carousel.go(`=${this.carousel.index - 1}`);
          //     instance.querySelector('button:last-child').setAttribute('disabled', 'true');
          //   }
          //   if (this.carousel.index === slides.length - 4) {
          //     instance.querySelector('button:last-child').setAttribute('disabled', 'true');
          //   }
          // }
        } else if (this.carousel.settings.perView < parseInt(this.perSlide, 10)) {
          // ENABLE IF USING 4-UP
          // if (window.innerWidth > _BREAKPOINTS_.lg) {
          //   if (this.carousel.index === slides.length - 4) {
          //     this.carousel.go(`=${this.carousel.index + 1}`);
          //   }
          // } else 
          if (window.innerWidth > _BREAKPOINTS_.md) {
            if (this.carousel.index === slides.length - 3) {
              this.carousel.go(`=${this.carousel.index + 1}`);
            }
          } else if (window.innerWidth > _BREAKPOINTS_.sm) {
            if (this.carousel.index === slides.length - 2) {
              this.carousel.go(`=${this.carousel.index + 1}`);
            }
          }
        }

        this.perSlide = this.carousel.settings.perView;
      }

    });

    this.initialRenderComplete = true;
  }

  private togglePause(e) {
    e.preventDefault();
    this.paused = !this.paused;

    if (this.paused) {
      this.carousel.pause();
    } else {
      this.carousel.play();
    }
  }

  render() {
    return (
      <div id={this.id} class="glide app-carousel">
        <div class="app-carousel-inner">
          <div class="glide__track" data-glide-el="track">
            <slot />
          </div>

          <div data-glide-el="controls" class="controls" hidden={this.hideControls}>
            <button data-glide-dir="<" aria-label="Previous Slide" aria-controls={this.id + '-slides'} type="button">
              <span class="icon-desktop d-none d-xl-block">
                <app-icon icon="skinny-chevron-left" />
              </span>
              <span class="icon-mobile d-block d-xl-none">
                <app-icon icon="chevron-left" />
              </span>
            </button>
            <button data-glide-dir=">" aria-label="Next Slide" aria-controls={this.id + '-slides'} type="button">
              <span class="icon-desktop d-none d-xl-block">
                <app-icon icon="skinny-chevron-right" />
              </span>
              <span class="icon-mobile d-block d-xl-none">
                <app-icon icon="chevron-right" />
              </span>
            </button>
          </div>
        </div>

        {this.bulletNav && (
          <div hidden={this.hideControls} class={`bullets-wrapper ${this.displayBulletNavExternally ? 'external' : 'internal'}`}>
            <div data-glide-el="controls" class="controls bullet-Arrow">
                      <button data-glide-dir="<" aria-label="Previous Slide" aria-controls={this.id + '-slides'} type="button">
                        <span class="d-xl-block">
                          <app-icon icon="skinny-chevron-left" />
                        </span>
                      </button>
                      <div class="glide__bullets" data-glide-el="controls[nav]"></div>
                      <button data-glide-dir=">" aria-label="Next Slide" aria-controls={this.id + '-slides'} type="button">
                        <span class="d-xl-block">
                          <app-icon icon="skinny-chevron-right" />
                        </span>
                      </button>
                </div>
            {this.autoPlay && (
              <button
                class="pause-action"
                aria-label={this.paused ? 'Play' : 'Pause'}
                aria-controls={this.id + '-slides'}
                title="Play/Pause"
                onClick={(e) => { this.togglePause(e) }}
                type="button">
                {this.paused && <app-icon icon="play" />}
                {!this.paused && <app-icon icon="pause" />}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

}
